import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, Paper, TextField, MenuItem, Grid,
  Alert, FormControlLabel, Switch, Divider, IconButton,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  Save as SaveIcon, ArrowBack as BackIcon,
  Add as AddIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { MovimientoTesoreriaService } from '../services/movimientoService';
import type { MovimientoTesoreriaDetalle } from '../types/movimiento';
import { initialMovimientoDetalle, TIPOS_MOVIMIENTO, TIPOS_AUXILIAR } from '../types/movimiento';
import { useCajaBancoStore } from '../../cajas-bancos/store/cajaBancoStore';

interface MovimientoFormData {
  idCajaBanco: number;
  tipoMovimiento: string;
  fechaMovimiento: string;
  idMoneda: number;
  tipoCambio: number;
  tipoAuxiliar: string;
  idPersona: number | null;
  nombreAuxiliar: string;
  idFormaPago: number | null;
  tipoOperacion: string;
  numeroOperacion: string;
  numeroCheque: string;
  glosa: string;
  esProvision: boolean;
}

const MovimientoFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isEdit = !!id && id !== 'nuevo';
  const tipoInicial = searchParams.get('tipo') || 'INGRESO';

  const { cajasBancos, fetchCajasBancos } = useCajaBancoStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<MovimientoFormData>({
    idCajaBanco: 0,
    tipoMovimiento: tipoInicial,
    fechaMovimiento: new Date().toISOString().split('T')[0],
    idMoneda: 0,
    tipoCambio: 1,
    tipoAuxiliar: '',
    idPersona: null,
    nombreAuxiliar: '',
    idFormaPago: null,
    tipoOperacion: '',
    numeroOperacion: '',
    numeroCheque: '',
    glosa: '',
    esProvision: false,
  });
  const [detalles, setDetalles] = useState<MovimientoTesoreriaDetalle[]>([]);

  useEffect(() => {
    fetchCajasBancos();
    if (isEdit && id) {
      loadMovimiento(parseInt(id));
    }
  }, [id, fetchCajasBancos, isEdit]);

  const loadMovimiento = async (movId: number) => {
    setLoading(true);
    try {
      const mov = await MovimientoTesoreriaService.getById(movId);
      setFormData({
        idCajaBanco: mov.idCajaBanco,
        tipoMovimiento: mov.tipoMovimiento,
        fechaMovimiento: mov.fechaMovimiento?.split('T')[0] || '',
        idMoneda: mov.idMoneda,
        tipoCambio: mov.tipoCambio,
        tipoAuxiliar: mov.tipoAuxiliar || '',
        idPersona: mov.idPersona,
        nombreAuxiliar: mov.nombreAuxiliar || '',
        idFormaPago: mov.idFormaPago,
        tipoOperacion: mov.tipoOperacion || '',
        numeroOperacion: mov.numeroOperacion || '',
        numeroCheque: mov.numeroCheque || '',
        glosa: mov.glosa || '',
        esProvision: mov.esProvision,
      });
      setDetalles(mov.detalles || []);
    } catch {
      setError('Error al cargar el movimiento');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof MovimientoFormData, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addDetalle = () => {
    setDetalles(prev => [
      ...prev,
      { ...initialMovimientoDetalle, numeroLinea: prev.length + 1 },
    ]);
  };

  const removeDetalle = (index: number) => {
    setDetalles(prev =>
      prev
        .filter((_, i) => i !== index)
        .map((d, i) => ({ ...d, numeroLinea: i + 1 }))
    );
  };

  const montoTotal = detalles.reduce((sum, d) => sum + (d.montoMonedaNacional || 0), 0);

  const handleSubmit = async () => {
    if (!formData.idCajaBanco) {
      setError('Seleccione una caja/banco');
      return;
    }
    if (detalles.length === 0) {
      setError('Agregue al menos un detalle');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await MovimientoTesoreriaService.save({
        ...formData,
        montoTotal,
        detalles,
      });
      navigate('/tesoreria/movimientos');
    } catch {
      setError('Error al guardar el movimiento');
    } finally {
      setLoading(false);
    }
  };

  const detalleColumns: GridColDef[] = [
    { field: 'numeroLinea', headerName: '#', width: 50 },
    { field: 'cuentaContable', headerName: 'Cuenta Contable', width: 150, editable: true },
    { field: 'tipoDocumento', headerName: 'Tipo Doc.', width: 100, editable: true },
    { field: 'serieDocumento', headerName: 'Serie', width: 80, editable: true },
    { field: 'numeroDocumento', headerName: 'Nro. Doc.', width: 120, editable: true },
    { field: 'montoMonedaNacional', headerName: 'Monto MN', width: 130, type: 'number', editable: true },
    { field: 'montoMonedaExtranjera', headerName: 'Monto ME', width: 130, type: 'number', editable: true },
    { field: 'glosa', headerName: 'Glosa', flex: 1, minWidth: 200, editable: true },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (_params) => {
        const rowIndex = detalles.findIndex(
          (d) => d.numeroLinea === _params.row.numeroLinea
        );
        return (
          <IconButton size="small" color="error" onClick={() => removeDetalle(rowIndex)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/tesoreria/movimientos')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isEdit
            ? 'Editar Movimiento'
            : `Nuevo ${formData.tipoMovimiento === 'INGRESO' ? 'Ingreso' : 'Egreso'}`}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Tipo Movimiento"
              fullWidth
              select
              value={formData.tipoMovimiento}
              onChange={(e) => handleChange('tipoMovimiento', e.target.value)}
            >
              {TIPOS_MOVIMIENTO.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Fecha"
              fullWidth
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.fechaMovimiento}
              onChange={(e) => handleChange('fechaMovimiento', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Caja/Banco"
              fullWidth
              select
              required
              value={formData.idCajaBanco || ''}
              onChange={(e) => handleChange('idCajaBanco', parseInt(e.target.value) || 0)}
            >
              <MenuItem value="">Seleccionar...</MenuItem>
              {cajasBancos
                .filter((cb) => cb.activo)
                .map((cb) => (
                  <MenuItem key={cb.idCajaBanco} value={cb.idCajaBanco}>
                    {cb.nombre}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Tipo Cambio"
              fullWidth
              type="number"
              value={formData.tipoCambio}
              onChange={(e) => handleChange('tipoCambio', parseFloat(e.target.value) || 1)}
              inputProps={{ step: 0.001 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField
              label="Tipo Auxiliar"
              fullWidth
              select
              value={formData.tipoAuxiliar}
              onChange={(e) => handleChange('tipoAuxiliar', e.target.value)}
            >
              <MenuItem value="">Ninguno</MenuItem>
              {TIPOS_AUXILIAR.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 8, md: 5 }}>
            <TextField
              label="Nombre Auxiliar"
              fullWidth
              value={formData.nombreAuxiliar}
              onChange={(e) => handleChange('nombreAuxiliar', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Tipo Operacion"
              fullWidth
              value={formData.tipoOperacion}
              onChange={(e) => handleChange('tipoOperacion', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Nro. Operacion"
              fullWidth
              value={formData.numeroOperacion}
              onChange={(e) => handleChange('numeroOperacion', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="Nro. Cheque"
              fullWidth
              value={formData.numeroCheque}
              onChange={(e) => handleChange('numeroCheque', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Glosa"
              fullWidth
              multiline
              rows={2}
              value={formData.glosa}
              onChange={(e) => handleChange('glosa', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.esProvision}
                  onChange={(e) => handleChange('esProvision', e.target.checked)}
                />
              }
              label="Es Provision"
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle del Movimiento</Typography>
          <Button startIcon={<AddIcon />} onClick={addDetalle}>
            Agregar Linea
          </Button>
        </Box>
        <Box sx={{ height: 300 }}>
          <DataGrid
            rows={detalles}
            columns={detalleColumns}
            getRowId={(row) => row.numeroLinea}
            hideFooter
            disableRowSelectionOnClick
            processRowUpdate={(newRow) => {
              const index = detalles.findIndex(
                (d) => d.numeroLinea === newRow.numeroLinea
              );
              if (index >= 0) {
                const updated = [...detalles];
                updated[index] = newRow;
                setDetalles(updated);
              }
              return newRow;
            }}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Typography variant="h6">
            Total: {montoTotal.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
          </Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button variant="outlined" onClick={() => navigate('/tesoreria/movimientos')}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>
    </Box>
  );
};

export default MovimientoFormPage;
